import { useAutoAnimate } from '@formkit/auto-animate/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import FormInput from 'components/common/form-input';
import Tooltip from 'components/layout/tooltip';
import { DoorItemType, SizingItemsType } from 'data/types';
import { useState } from 'react';
import { isNotEmpty } from 'utils/helpers';
import {
  getValidSizingAttributeOptions,
  isValidSizingItem
} from 'utils/sizing';

type SolidDoorProps = {
  door: DoorItemType;
  updateFn: (doorId: string, updates: Partial<DoorItemType>) => void;
  duplicateFn: (doorId: string) => void;
  deleteFn: (doorId: string) => void;
  updateDefaultSizingFn: (updates: Partial<SizingItemsType>) => void;
  defaultExpandAdvanced?: boolean;
};

export default function SolidDoorComponent({
  door,
  updateFn,
  duplicateFn,
  deleteFn,
  updateDefaultSizingFn,
  defaultExpandAdvanced = false
}: SolidDoorProps) {
  const defaultValues = useAppSelector(
    (state) => state.data.defaultSizingItems.solidDoor
  );
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(
    defaultExpandAdvanced
  );
  const [animate] = useAutoAnimate({ duration: 100 });
  const [deleteQueued, setDeleteQueued] = useState(false);
  const sizingItemType = 'solidDoors';
  const isModified = Object.keys(defaultValues)
    .filter((key) => isNotEmpty(defaultValues[key]))
    .some((key) => door[key] !== defaultValues[key]);

  const handleUpdateWithValidation = (changes: Partial<DoorItemType>) => {
    const newDoor: DoorItemType = {
      ...door,
      ...changes
    };
    const isValid = isValidSizingItem(sizingItemType, newDoor);

    updateFn(door.uniqueId, {
      ...newDoor,
      valid: isValid
    });
  };

  const toggleAdvancedOptions = () => {
    setShowAdvancedOptions((prev) => !prev);
  };

  const handleDuplicateClick = () => {
    duplicateFn(door.uniqueId);
  };

  const handleDeleteClick = () => {
    if (!deleteQueued) {
      setDeleteQueued(true);
      return;
    }

    deleteFn(door.uniqueId);
    setDeleteQueued(false);
  };

  const setAsDefault = () => {
    updateDefaultSizingFn({
      solidDoor: {
        ...door,
        uniqueId: undefined,
        valid: undefined
      }
    });
  };

  const classes = clsx(
    'flex w-full flex-col divide-y divide-gray-300 rounded-xl bg-gray-200 px-5 border-2',
    door.valid ? 'border-gray-200' : 'border-red-500'
  );

  return (
    <div
      key={door.uniqueId}
      id={`fenestration-${door.uniqueId}`}
      className={classes}
      ref={animate}
    >
      <div className='flex min-h-[54px] items-center gap-5'>
        <div className='font-semibold'>{door.material} Door</div>
        <div className='w-[130px]'>
          <FormInput
            value={door.width}
            placeholder='Width (ft)'
            prefix='Width: '
            type='number'
            suffix=' ft'
            min={1}
            onChange={(value) => handleUpdateWithValidation({ width: value })}
            step={0.25}
            required
          />
        </div>
        <div className='w-[130px]'>
          <FormInput
            value={door.height}
            placeholder='Height (ft)'
            prefix='Height: '
            type='number'
            suffix=' ft'
            min={1}
            onChange={(value) => handleUpdateWithValidation({ height: value })}
            step={0.25}
            required
          />
        </div>
        {!door.valid && (
          <span className='text-red-500'>Invalid Attributes!</span>
        )}
        <div className='ml-auto flex items-center gap-2'>
          <button
            className='relative flex h-full cursor-pointer items-center text-gray-500 hover:text-gray-700'
            type='button'
            onClick={toggleAdvancedOptions}
          >
            <span>Advanced</span>
            {showAdvancedOptions ? (
              <ChevronUpIcon className='h-5 w-5' />
            ) : (
              <ChevronDownIcon className='h-5 w-5' />
            )}
            <Tooltip
              text={
                showAdvancedOptions
                  ? 'Hide Advanced Options'
                  : 'Show Advanced Options'
              }
            />
          </button>
          <button
            className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700'
            type='button'
            onClick={handleDuplicateClick}
          >
            <DocumentDuplicateIcon className='h-5 w-5' />
            <Tooltip text='Duplicate Door' />
          </button>
          <button
            className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700'
            type='button'
            onClick={handleDeleteClick}
            onMouseLeave={() => setDeleteQueued(false)}
          >
            <TrashIcon className='h-5 w-5' />
            <Tooltip
              text={deleteQueued ? 'Click Again to Confirm' : 'Delete Door'}
            />
          </button>
        </div>
      </div>
      {showAdvancedOptions && (
        <div className='flex flex-wrap items-center gap-x-5 gap-y-3 py-3'>
          <FormInput
            value={door.material}
            label='Material'
            type='select'
            options={getValidSizingAttributeOptions(
              sizingItemType,
              door,
              'material'
            )}
            onChange={(value) => {
              handleUpdateWithValidation({
                material: value
              });
            }}
            required
            preventDeselection
          />
          <FormInput
            value={door.stormMaterial}
            label='Storm Material'
            type='select'
            options={getValidSizingAttributeOptions(
              sizingItemType,
              door,
              'stormMaterial'
            )}
            onChange={(value) => {
              handleUpdateWithValidation({
                stormMaterial: value
              });
            }}
            required
            preventDeselection
          />
          <FormInput
            value={door.core}
            label='Core'
            type='select'
            options={getValidSizingAttributeOptions(
              sizingItemType,
              door,
              'core'
            )}
            onChange={(value) => {
              handleUpdateWithValidation({
                core: value
              });
            }}
            required
            preventDeselection
          />
          {isModified && (
            <div className='flex w-full justify-end'>
              <button
                className='relative flex h-full cursor-pointer items-center text-gray-500 hover:text-gray-700'
                type='button'
                onClick={setAsDefault}
              >
                <span>Set as Default</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
